import { useEffect, useMemo, useState, useSyncExternalStore } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { useQuery } from "@tanstack/react-query";

import { initUseQuery, queryKey } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { List, ListItem } from "simplydo/react/ui/list";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import type {
  IdeaAssessment,
  UseAssessmentEditorReturn,
} from "components/ideas/types";
import type {
  IdeaCustomFieldIdea,
  IdeaWithIdeaTemplate,
} from "components/lib/Ideas/IdeaPreview/types";

import { reducePath } from "./useIdeaEditor";

type AssessmentRound = NonNullable<
  NonNullable<OpenAPI.Schemas["IdeaTemplate"]["assessments"]>["rounds"]
>[number];

const pendingSaves = new Map<
  string,
  { timeout: ReturnType<typeof setTimeout>; assessment: IdeaAssessment }
>();
// Saves for one idea and round run one after another, so submitting can wait
// for the last answer to be stored and responses never arrive out of order.
const saveQueues = new Map<string, Promise<boolean>>();
// A response only replaces the cached assessment when no later save is queued,
// or it would undo answers given since. Every field has its own editor, so
// they all read whether a save is under way from here.
const queuedSaveCounts = new Map<string, number>();
const saveListeners = new Set<() => void>();

const subscribeToSaves = (listener: () => void) => {
  saveListeners.add(listener);
  return () => {
    saveListeners.delete(listener);
  };
};

const setQueuedSaveCount = (saveKey: string, count: number) => {
  if (count > 0) queuedSaveCounts.set(saveKey, count);
  else queuedSaveCounts.delete(saveKey);
  saveListeners.forEach((listener) => listener());
};

const settleQueuedSave = (saveKey: string) => {
  const remaining = (queuedSaveCounts.get(saveKey) ?? 1) - 1;
  setQueuedSaveCount(saveKey, remaining);
  return remaining;
};

export const useAssessmentEditor = (
  idea: IdeaWithIdeaTemplate | IdeaCustomFieldIdea | null,
): UseAssessmentEditorReturn => {
  // Covers the whole submission, including the last answer's save it waits
  // for, so the submit buttons stay busy and a second click is ignored.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const ideaId = idea?._id;
  const { t } = useTranslation();
  const [params, setSearchParams] = useSearchParams();

  const user = useCurrentUser();

  const assessmentRounds = useMemo(
    () => idea?.ownerIdeaTemplate?.assessments?.rounds || [],
    [idea?.ownerIdeaTemplate?.assessments?.rounds],
  );
  const paramsRoundId = params.get("assessmentRound") || "";

  const [isAssessor, roundsToAssess] = useMemo(() => {
    let isAssessor = false;
    const roundsToAssess: Set<string> = new Set();
    idea?.assessments?.forEach((assessment: IdeaAssessment) => {
      if (assessment.user === user?._id) {
        isAssessor = true;
        if (assessment.assessmentRoundId) {
          roundsToAssess.add(assessment.assessmentRoundId);
        }
      }
    });
    return [isAssessor, Array.from(roundsToAssess)];
  }, [idea, user]);

  useEffect(() => {
    if (!idea?.ownerIdeaTemplate || !paramsRoundId) {
      return;
    }
    const roundExists = assessmentRounds.some(
      (round: AssessmentRound) => round.id === paramsRoundId,
    );
    if (!roundExists) {
      setSearchParams((prevParams) => {
        prevParams.delete("assessmentRound");
        return prevParams;
      });
    }
  }, [
    assessmentRounds,
    idea?.ownerIdeaTemplate,
    paramsRoundId,
    setSearchParams,
  ]);

  const defaultAssessmentRoundId = useMemo(() => {
    const firstAssignedRound = assessmentRounds.find((round) =>
      roundsToAssess.includes(round.id),
    );
    return firstAssignedRound?.id || assessmentRounds[0]?.id || "";
  }, [assessmentRounds, roundsToAssess]);

  const selectedAssessmentRoundId = paramsRoundId || defaultAssessmentRoundId;

  const isAssessingSelectedRound = useMemo(() => {
    return roundsToAssess.includes(selectedAssessmentRoundId);
  }, [roundsToAssess, selectedAssessmentRoundId]);

  const assessmentQuery = useQuery(
    initUseQuery(
      api.ideas.getAssessment,
      [ideaId!, selectedAssessmentRoundId],
      {
        isEnabled: !!ideaId && isAssessor,
        initialData: idea?.assessments?.find(
          (assessment: IdeaAssessment) =>
            assessment.assessmentRoundId === selectedAssessmentRoundId &&
            user?._id === assessment.user,
        ),
      },
    ),
  );

  const setAssessmentRound = (newRoundId: string) => {
    setSearchParams((prevParams) => {
      prevParams.set("assessmentRound", newRoundId);
      return prevParams;
    });
  };

  const clearAssessmentRound = () => {
    setSearchParams((prevParams) => {
      prevParams.delete("assessmentRound");
      return prevParams;
    });
  };

  const saveKey = `${ideaId}:${selectedAssessmentRoundId}`;
  const isSaving = useSyncExternalStore(subscribeToSaves, () =>
    queuedSaveCounts.has(saveKey),
  );
  const assessmentQueryKey = queryKey(api.ideas.getAssessment, [
    ideaId!,
    selectedAssessmentRoundId,
  ]);

  const sendAssessment = (updatedAssessment: IdeaAssessment) =>
    new Promise<boolean>((resolve) => {
      api.ideas.updateAssessment(
        ideaId!,
        selectedAssessmentRoundId,
        // OpenAPI PUT body for this route is incorrectly spec'd; runtime payload is IdeaAssessment.
        updatedAssessment as never,
        (data) => {
          const remaining = settleQueuedSave(saveKey);
          if (remaining === 0 && !pendingSaves.has(saveKey)) {
            api.setQueryData(
              api.ideas.getAssessment,
              [ideaId!, selectedAssessmentRoundId],
              data.assessment,
            );
          }
          const challengeId = idea?.challenge;
          if (challengeId) {
            api.invalidateQueries(api.challenges.getAssignedAssessments, [
              challengeId,
            ]);
          }
          resolve(true);
        },
        (err) => {
          toast.error(err.message);
          settleQueuedSave(saveKey);
          resolve(false);
        },
      );
    });

  const saveAssessment = (updatedAssessment: IdeaAssessment) => {
    setQueuedSaveCount(saveKey, (queuedSaveCounts.get(saveKey) ?? 0) + 1);
    const queued = (saveQueues.get(saveKey) ?? Promise.resolve(true)).then(() =>
      sendAssessment(updatedAssessment),
    );
    saveQueues.set(saveKey, queued);
    void queued.then(() => {
      if (saveQueues.get(saveKey) === queued) saveQueues.delete(saveKey);
    });
    return queued;
  };

  const flushAssessmentSave = () => {
    const pending = pendingSaves.get(saveKey);
    if (pending) {
      clearTimeout(pending.timeout);
      pendingSaves.delete(saveKey);
      return saveAssessment(pending.assessment);
    }
    return saveQueues.get(saveKey) ?? Promise.resolve(true);
  };

  /*
    Path is as follows: ['assessment_object', 'field_id', 'property']
  */
  const updateAssessment = (
    update: unknown,
    path: string[],
    suppressSave = false,
  ) => {
    const prevAssessment =
      api.queryClient.getQueryData<IdeaAssessment>(assessmentQueryKey) ||
      assessmentQuery?.data ||
      {};
    const updatedAssessment = reducePath(
      prevAssessment as Record<string, unknown>,
      update as Partial<Record<string, unknown>>,
      path,
    ) as IdeaAssessment;

    api.setQueryData(
      api.ideas.getAssessment,
      [ideaId!, selectedAssessmentRoundId],
      updatedAssessment,
    );
    const challengeId = idea?.challenge;
    if (challengeId) {
      api.setQueryData(
        api.challenges.getAssignedAssessments,
        [challengeId],
        (prev) => {
          const newIdeas = (prev?.ideas || []).map((prevIdea) => {
            if (prevIdea._id === ideaId) {
              return {
                ...prevIdea,
                assessments: prevIdea.assessments?.map((oldAssessment) => {
                  if (
                    oldAssessment.user === updatedAssessment.user &&
                    oldAssessment.assessmentRoundId ===
                      selectedAssessmentRoundId
                  ) {
                    return updatedAssessment;
                  }
                  return oldAssessment;
                }),
              };
            }
            return prevIdea;
          });
          return { ...prev, ideas: newIdeas };
        },
      );
    }

    // Every field mounts its own editor, so the debounce is shared per idea and round to send one PUT
    clearTimeout(pendingSaves.get(saveKey)?.timeout);
    pendingSaves.delete(saveKey);
    if (suppressSave) return;
    pendingSaves.set(saveKey, {
      assessment: updatedAssessment,
      timeout: setTimeout(() => {
        pendingSaves.delete(saveKey);
        void saveAssessment(updatedAssessment);
      }, 400),
    });
  };

  const submitAssessment = () => {
    let scoredAll = true;

    const prompt = assessmentQuery?.data?.conflictOfInterest
      ?.hasConflictOfInterest ? (
      <div>
        Once submitted you will not be able to change your assessment of this{" "}
        {t("generic.idea")}.
        <br />
        <br />
        You have declared a conflict of interest. When you submit this
        assessment, no assessment scores will be submitted and the{" "}
        {t("generic.challenge")} managers will be notified. Are you sure you
        want to proceed?
      </div>
    ) : (
      <div>
        Once submitted you will not be able to change your assessment of this{" "}
        {t("generic.idea")}. You have given the following scores:
        <List variant="bulleted" compact>
          {idea?.ownerIdeaTemplate?.body?.flatMap((section) => {
            if (
              section.type !== "assessment" ||
              section.assessmentRoundId !== selectedAssessmentRoundId
            ) {
              return [];
            }

            return (section.fields ?? [])
              .filter((field) => field.type === "assessment")
              .map((field) => {
                const fieldScore =
                  assessmentQuery.data?.assessment?.[field.id]?.score;
                // If the assessment field has not been scored at all it is undefined - only in this case we reject submission
                if (fieldScore === undefined) {
                  scoredAll = false;
                }
                return (
                  <ListItem key={field.title}>
                    {field.title}: {fieldScore ?? 0} /{" "}
                    {Array.isArray(field.options)
                      ? 0
                      : field.options?.availableScore || 0}
                  </ListItem>
                );
              });
          })}
        </List>
        If you are happy with these scores, click submit now. If you would like
        to change anything, please cancel and amend.
      </div>
    );

    if (!scoredAll) {
      toast.error("You must score all fields before submitting");
      return;
    }

    util
      .confirm("Ready to submit your assessment?", prompt)
      .then(async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        const loadingId = toast.loading("Submitting assessment...");
        if (!(await flushAssessmentSave())) {
          toast.dismiss(loadingId);
          setIsSubmitting(false);
          return;
        }

        api.ideas.submitAssessment(
          ideaId!,
          selectedAssessmentRoundId,
          ({ assessment: newAssessment }) => {
            updateAssessment(newAssessment.isSubmitted, ["isSubmitted"], true);
            updateAssessment(newAssessment.submittedAt, ["submittedAt"], true);
            const challengeId = idea?.challenge;
            if (challengeId) {
              api.invalidateQueries(api.challenges.getAssignedAssessments, [
                challengeId,
              ]);
              api.invalidateQueries(api.challenges.getAssessmentDashboard, [
                challengeId,
              ]);
            }
            api.invalidateQueries(api.ideas.getAssessment, [
              ideaId!,
              selectedAssessmentRoundId,
            ]);
            toast.success("Assessment submitted", { id: loadingId });
            setIsSubmitting(false);
          },
          (err) => {
            toast.error(`Unable to submit your assessment: ${err.message}`, {
              id: loadingId,
            });
            setIsSubmitting(false);
          },
        );
      })
      .catch(() => {});
  };

  return {
    isAssessor,
    isAssessingSelectedRound,
    roundsToAssess,
    selectedAssessmentRoundId,
    urlAssessmentRoundId: paramsRoundId,
    isAssessmentLoading: assessmentQuery.isLoading,
    assessmentState: assessmentQuery.data,
    assessmentIsSubmissable: util.assessmentIsSubmissable(
      assessmentQuery.data,
      idea?.ownerIdeaTemplate,
    ),
    updateAssessment,
    submitAssessment,
    isSavingAssessment: isSaving || isSubmitting,
    setAssessmentRound,
    clearAssessmentRound,
  };
};
